'use client';

import { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  TicketQuestion,
  QuestionAnswer,
  questionLabel,
  sortQuestions,
  validateAnswers,
  isWithinTicketDocumentSize,
  ticketDocumentExtension,
  TICKET_DOCUMENT_ACCEPT,
  TICKET_DOCUMENT_FORMATS,
  TICKET_DOCUMENT_MAX_MB,
} from '@/lib/tickets/questions';
import { useToast } from '@/lib/context/ToastContext';
import { FileUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { getCurrentUser } from '@/lib/auth/currentUser';

interface TicketCustomFieldsFormProps {
  ticketId: string;
  ticketName: string;
  questions: TicketQuestion[];
  onComplete: (responses: QuestionAnswer[]) => void;
  onCancel?: () => void;
  /**
   * True when submitting this form completes the registration outright (free
   * ticket, no payment step). Paid tickets still go on to checkout, so they keep
   * the neutral "Continue" label.
   */
  isFinalStep?: boolean;
}

export default function TicketCustomFieldsForm({
  ticketId,
  ticketName,
  questions,
  onComplete,
  onCancel,
  isFinalStep = false
}: TicketCustomFieldsFormProps) {
  const { t, language } = useLanguage();
  const sortedQuestions = sortQuestions(questions);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File>>({});
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();
  const supabase = createClient();

  const questionPlaceholder = (question: TicketQuestion): string | undefined => {
    if (language === 'en' && question.placeholder_en) return question.placeholder_en;
    return question.placeholder_ua ?? undefined;
  };

  const handleFileChange = (questionId: string, file: File | null) => {
    // Reject an oversized file here, where we can name the limit, instead of
    // letting the bucket refuse it later with an untranslated storage error.
    if (file && !isWithinTicketDocumentSize(file.size)) {
      setFiles(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setErrors(prev => ({ ...prev, [questionId]: 'ticketQuestions.errorFileTooLarge' }));
      toast.error(t('ticketQuestions.errorFileTooLarge', { size: String(TICKET_DOCUMENT_MAX_MB) }));
      return;
    }

    setFiles(prev => {
      const next = { ...prev };
      if (file) {
        next[questionId] = file;
      } else {
        delete next[questionId];
      }
      return next;
    });
    setErrors(prev => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const uploadFile = async (questionId: string, file: File): Promise<string> => {
    const user = getCurrentUser();
    if (!user) throw new Error(t('ticketCustomFields.failedToUploadFile', { error: t('ticketCustomFields.noActiveSession') }));

    // The readback route only signs plainly well-formed keys
    // (isWellFormedTicketDocumentPath), so the extension has to be forced
    // into that character set here - a real upload named "scan.pdf " or
    // "паспорт.документ" would otherwise produce a key the signing route has
    // to refuse, 403ing the participant's own document. A name that carries no
    // usable extension falls back to the file's mime type rather than to a
    // meaningless ".bin" - see ticketDocumentExtension.
    const fileName = `${crypto.randomUUID()}.${ticketDocumentExtension(file.name, file.type)}`;
    // First segment must be the user id - the storage policy matches on it.
    const filePath = `${user.id}/${ticketId}/${questionId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('ticket-documents')
      .upload(filePath, file);

    if (uploadError) {
      throw new Error(t('ticketCustomFields.failedToUploadFile', { error: uploadError.message }));
    }

    // Private bucket: store the path, not a public URL. Readback is signed on demand.
    return filePath;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate using current local state - for file questions we can't know the
    // final fileUrl until after upload, so a selected-but-not-yet-uploaded file
    // stands in as a truthy placeholder purely to satisfy the "is this answered"
    // check in validateAnswer.
    const preliminaryAnswers: QuestionAnswer[] = sortedQuestions.map(question => ({
      questionId: question.id,
      value: question.field_type === 'file' ? null : (responses[question.id] ?? null),
      fileUrl: question.field_type === 'file' ? (files[question.id] ? 'pending-upload' : null) : null,
    }));

    const validationErrors = validateAnswers(sortedQuestions, preliminaryAnswers);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(t('ticketCustomFields.fixErrorsBeforeSubmitting'));
      return;
    }

    setErrors({});
    setUploading(true);

    try {
      const finalAnswers: QuestionAnswer[] = [];

      for (const question of sortedQuestions) {
        if (question.field_type === 'file') {
          const file = files[question.id];
          const fileUrl = file ? await uploadFile(question.id, file) : null;
          finalAnswers.push({ questionId: question.id, value: null, fileUrl });
        } else {
          const raw = responses[question.id];
          finalAnswers.push({
            questionId: question.id,
            value: raw && raw.trim() !== '' ? raw : null,
            fileUrl: null,
          });
        }
      }

      onComplete(finalAnswers);
    } catch (error) {
      console.error('Error submitting custom fields:', error);
      toast.error(error instanceof Error ? error.message : t('ticketCustomFields.failedToSubmitForm'));
    } finally {
      // Reset on every exit path, success included: the caller may keep this
      // form mounted while it does follow-up work, and a failure there must
      // not leave the submit button disabled forever.
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('ticketCustomFields.title')}
          </h2>
          <p className="text-gray-600">
            {t('ticketCustomFields.descriptionForTicket', { ticketName })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {sortedQuestions.map((question) => {
            const label = questionLabel(question, language);
            const errorKey = errors[question.id];
            const errorMessage = errorKey ? t(errorKey) : undefined;

            return (
              <div key={question.id}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {label}
                  {question.is_required && <span className="text-red-600">*</span>}
                </label>

                {/* Short Text */}
                {question.field_type === 'text' && (
                  <Input
                    type="text"
                    value={responses[question.id] || ''}
                    onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                    placeholder={questionPlaceholder(question)}
                    error={errorMessage}
                  />
                )}

                {/* Essay (Long Text) */}
                {question.field_type === 'essay' && (
                  <Textarea
                    value={responses[question.id] || ''}
                    onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                    placeholder={questionPlaceholder(question)}
                    rows={4}
                    error={errorMessage}
                  />
                )}

                {/* Single Choice (Radio) */}
                {question.field_type === 'single_choice' && (
                  <div className="space-y-2">
                    {question.field_options.map((option) => (
                      <label key={option} className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="radio"
                          name={question.id}
                          value={option}
                          checked={responses[question.id] === option}
                          onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Multiple Choice (Checkbox) */}
                {question.field_type === 'multiple_choice' && (
                  <div className="space-y-2">
                    {question.field_options.map((option) => (
                      <label key={option} className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          value={option}
                          checked={(responses[question.id] || '').split(',').includes(option)}
                          onChange={(e) => {
                            const currentValues = (responses[question.id] || '').split(',').filter(v => v);
                            const newValues = e.target.checked
                              ? [...currentValues, option]
                              : currentValues.filter(v => v !== option);
                            setResponses({ ...responses, [question.id]: newValues.join(',') });
                          }}
                          className="h-4 w-4 rounded"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* True/False */}
                {question.field_type === 'true_false' && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="radio"
                        name={question.id}
                        value="true"
                        checked={responses[question.id] === 'true'}
                        onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">{t('ticketCustomFields.trueOption')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="radio"
                        name={question.id}
                        value="false"
                        checked={responses[question.id] === 'false'}
                        onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">{t('ticketCustomFields.falseOption')}</span>
                    </label>
                  </div>
                )}

                {/* File Upload */}
                {question.field_type === 'file' && (
                  <div>
                    {/* State the rules before a file is chosen, rather than
                        letting the bucket reject it after the fact. */}
                    <p className="text-xs text-gray-500 mb-2">
                      {t('ticketQuestions.fileRules', {
                        formats: TICKET_DOCUMENT_FORMATS,
                        size: String(TICKET_DOCUMENT_MAX_MB),
                      })}
                    </p>
                    <div className={`relative border-2 border-dashed rounded-lg p-6 text-center ${
                      errorMessage ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                    }`}>
                      <input
                        type="file"
                        accept={TICKET_DOCUMENT_ACCEPT}
                        onChange={(e) => handleFileChange(question.id, e.target.files?.[0] || null)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <FileUp className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      {files[question.id] ? (
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm text-gray-700">{files[question.id].name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileChange(question.id, null);
                            }}
                            className="text-red-600 hover:text-red-700 h-auto p-1"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          {t('ticketCustomFields.clickToUpload')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Error Message - only for field types that don't have built-in error display */}
                {errorMessage && question.field_type !== 'text' && question.field_type !== 'essay' && (
                  <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
                )}
              </div>
            );
          })}

          <div className="flex gap-3 pt-4 border-t">
            {onCancel && (
              <Button
                variant="secondary"
                onClick={onCancel}
                disabled={uploading}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
            )}
            <Button type="submit" disabled={uploading} className="flex-1">
              {uploading
                ? t('ticketCustomFields.uploading')
                : isFinalStep
                  ? t('ticketCustomFields.finishRegistration')
                  : t('ticketCustomFields.continue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
